import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface ITabState {
  tabId: string;
  rightAside?: {
    openTab: 'IO' | 'FEEDBACK' | 'TEST_CASES' | 'NOTES' | 'HISTORY' | 'AI' | '';
  };
  isLocked?: boolean;
  runSampleTests?: {
    testCasesWithTokens?: {
      test_case_id: string;
      input: string;
      output: string;
      token: string;
    }[];
    code?: string;
    isLoading?: boolean;
    languageId?: string;
  };
  runHiddenTests?: {
    testCasesWithTokens?: {
      test_case_id: string;
      input: string;
      output: string;
      token: string;
    }[];
    code?: string;
    isLoading?: boolean;
    languageId?: string;
  };

  generateQuestions?: {
    token?: string[] | null;
    attempt?: number;
    isLoading?: boolean;
  };
  runCode?: {
    codeRunToken?: string | null;
    code?: string;
    isLoading?: boolean;
    languageId?: string;
    framework?: number;
  };
  isWhiteBoardOpen?: boolean;
  leftAside?: {
    isQuestionsActive?: boolean;
    isImportQuestionsOpen?: boolean;
  };
  isLanguagesEnv?: {
    isLanguagesEnvOpen: boolean;
    iframeUrl: string | null;
    podName?: string | null;
    stack?: string | null;
    stackName?: string | null;
    previewUrl?: string | null;
    iconUrl?: string | null;
    iconUrls?: string[] | null;
    errorMessage?: string | null;
    warningMessage?: string | null;
    loadingMessage?: string | null;
  };
}

type LanguagesEnvState = NonNullable<ITabState['isLanguagesEnv']>;
type LanguagesEnvUpdatePayload = Partial<LanguagesEnvState> & {
  tabId?: string;
};

const initialState: {
  activeTab: string;
  code: string;
  getCode: string;
  tabs: ITabState[];
  userLog: { id: string; color?: string | undefined }[];
  rightAside: {
    openTab: 'IO' | 'FEEDBACK' | 'TEST_CASES' | 'NOTES' | 'HISTORY' | 'AI' | '';
  };
  video: {
    isMaximized: boolean;
    expandedId: string;
    isContentExpanded: boolean;
  };
  isLanguagesEnv?: {
    isLanguagesEnvOpen: boolean;
    iframeUrl: string | null;
    podName?: string | null;
    stack?: string | null;
    iconUrl?: string | null;
    iconUrls?: string[] | null;
    errorMessage?: string | null;
    warningMessage?: string | null;
    loadingMessage?: string | null;
  };
} = {
  activeTab: '',
  code: '',
  getCode: '',
  userLog: [],
  tabs: [],
  rightAside: {
    openTab: 'IO',
  },
  video: {
    isMaximized: false,
    isContentExpanded: false,
    expandedId: '',
  },
  isLanguagesEnv: {
    isLanguagesEnvOpen: false,
    iframeUrl: null,
    errorMessage: null,
    warningMessage: null,
    loadingMessage: null,
  },
};

export const tabsStateSlice = createSlice({
  name: 'tabsState',
  initialState,
  reducers: {
    setActiveTab: (state, action: PayloadAction<string>) => {
      // AI Assist is a global panel (its agents live in Firebase, shared across
      // question tabs), so unlike the per-tab IO/History/Notes panes it should
      // stay open as the user switches question tabs. Remember if we're leaving
      // it. (The 'AI' value only ever exists when the AI Assist feature is on.)
      const leavingAiAssist = state.rightAside?.openTab === 'AI';

      // Save current rightAside into the tab we're leaving
      if (state.activeTab) {
        const currentTab = state.tabs.find(
          (tab) => tab.tabId === state.activeTab
        );
        if (currentTab) {
          currentTab.rightAside = { ...state.rightAside };
        }
      }

      state.activeTab = action.payload;

      const isTabPresent = state.tabs.find(
        (tab) => tab.tabId === action.payload
      );
      if (!isTabPresent) {
        state.tabs.push({
          tabId: action.payload,
          runCode: {
            codeRunToken: null,
          },
        });
      }

      // Restore the incoming tab's saved rightAside, or default to 'IO' for new tabs
      const incomingTab = state.tabs.find(
        (tab) => tab.tabId === action.payload
      );
      state.rightAside = incomingTab?.rightAside ?? { openTab: 'IO' };

      // Keep AI Assist open across question tabs (global panel — see above).
      if (leavingAiAssist) {
        state.rightAside = { openTab: 'AI' };
      }
    },
    removeTab: (state, action: PayloadAction<string>) => {
      state.tabs = state.tabs.filter((tab) => tab.tabId !== action.payload);
    },
    updateRunCode: (state, action: PayloadAction<ITabState['runCode']>) => {
      const tabToUpdate = state.tabs.find(
        (tab) => tab.tabId === state.activeTab
      );

      if (tabToUpdate && tabToUpdate.runCode) {
        tabToUpdate.runCode = { ...tabToUpdate.runCode, ...action.payload };
      }
    },
    updateSampleRunTests: (
      state,
      action: PayloadAction<ITabState['runSampleTests']>
    ) => {
      const tabToUpdate = state.tabs.find(
        (tab) => tab.tabId === state.activeTab
      );

      if (tabToUpdate) {
        tabToUpdate.runSampleTests = {
          ...tabToUpdate.runSampleTests,
          ...action.payload,
        };
      }
    },
    updateHiddenRunTests: (
      state,
      action: PayloadAction<ITabState['runHiddenTests']>
    ) => {
      const tabToUpdate = state.tabs.find(
        (tab) => tab.tabId === state.activeTab
      );

      if (tabToUpdate) {
        tabToUpdate.runHiddenTests = {
          ...tabToUpdate.runHiddenTests,
          ...action.payload,
        };
      }
    },
    toggleWhiteBoard: (state) => {
      const tabToUpdate = state.tabs.find(
        (tab) => tab.tabId === state.activeTab
      );

      if (tabToUpdate) {
        tabToUpdate.isWhiteBoardOpen = !tabToUpdate.isWhiteBoardOpen;
      }
    },
    updateLeftAsideTabState: (
      state,
      action: PayloadAction<ITabState['leftAside']>
    ) => {
      const tabToUpdate = state.tabs.find(
        (tab) => tab.tabId === state.activeTab
      );

      if (tabToUpdate) {
        tabToUpdate.leftAside = { ...tabToUpdate.leftAside, ...action.payload };
      }
    },
    updateRightAsideTabState: (
      state,
      action: PayloadAction<{
        openTab:
          | 'IO'
          | 'FEEDBACK'
          | 'TEST_CASES'
          | 'NOTES'
          | 'HISTORY'
          | 'AI'
          | '';
      }>
    ) => {
      state.rightAside = action.payload;
      // Persist to the active tab so it survives tab switching
      const tabToUpdate = state.tabs.find(
        (tab) => tab.tabId === state.activeTab
      );
      if (tabToUpdate) {
        tabToUpdate.rightAside = action.payload;
      }
    },
    updateUserLog: (
      state,
      action: PayloadAction<{ id: string; color?: string | undefined }[]>
    ) => {
      state.userLog = action.payload;
    },
    maximizeVideo: (state, action: PayloadAction<{ attendeeId: string }>) => {
      state.video = {
        ...state.video,
        isMaximized: true,
        expandedId: action.payload.attendeeId,
        isContentExpanded: false,
      };
    },
    minimizeVideo: (state) => {
      state.video = { ...state.video, isMaximized: false, expandedId: '' };
    },
    minimizeContent: (state) => {
      state.video = { ...state.video, isContentExpanded: false };
    },
    maximizeContent: (state) => {
      state.video = {
        ...state.video,
        isContentExpanded: true,
        isMaximized: false,
        expandedId: '',
      };
    },
    // This Updates The Editor Code Be Careful when using this
    updateCode: (state, action: PayloadAction<string>) => {
      state.code = action.payload;
    },
    // This just contains the latest value of editor code
    updateGetCode: (state, action: PayloadAction<string>) => {
      state.getCode = action.payload;
    },
    updateGenerateQuestions: (
      state,
      action: PayloadAction<ITabState['generateQuestions']>
    ) => {
      const tabToUpdate = state.tabs.find(
        (tab) => tab.tabId === state.activeTab
      );

      if (tabToUpdate) {
        tabToUpdate.generateQuestions = {
          ...tabToUpdate.generateQuestions,
          ...action.payload,
        };
      }
    },
    setTabLocked: (
      state,
      action: PayloadAction<{ tabId: string; isLocked: boolean }>
    ) => {
      const tab = state.tabs.find((t) => t.tabId === action.payload.tabId);
      if (tab) {
        tab.isLocked = action.payload.isLocked;
      }
    },
    updateLanguagesEnvState: (
      state,
      action: PayloadAction<LanguagesEnvUpdatePayload>
    ) => {
      const { tabId, ...payload } = action.payload ?? {};
      const targetTabId = tabId ?? state.activeTab;
      const tabToUpdate = state.tabs.find((tab) => tab.tabId === targetTabId);

      if (tabToUpdate) {
        // Merge: keep existing values, apply only the fields explicitly provided in payload.
        // Filter out `undefined` so callers can omit fields without clobbering existing data,
        // while still allowing explicit `null` to clear a field.
        const definedUpdates = Object.fromEntries(
          Object.entries(payload).filter(([, v]) => v !== undefined)
        ) as Partial<LanguagesEnvState>;

        const existing = tabToUpdate.isLanguagesEnv;
        tabToUpdate.isLanguagesEnv = {
          isLanguagesEnvOpen: existing?.isLanguagesEnvOpen ?? false,
          iframeUrl: existing?.iframeUrl ?? null,
          podName: existing?.podName ?? null,
          stack: existing?.stack ?? null,
          previewUrl: existing?.previewUrl ?? null,
          iconUrl: existing?.iconUrl ?? null,
          iconUrls: existing?.iconUrls ?? null,
          errorMessage: existing?.errorMessage ?? null,
          warningMessage: existing?.warningMessage ?? null,
          loadingMessage: existing?.loadingMessage ?? null,
          ...definedUpdates,
        };
      }
    },
  },
});

export const {
  maximizeVideo,
  minimizeVideo,
  maximizeContent,
  minimizeContent,
  setActiveTab,
  updateRunCode,
  updateSampleRunTests,
  updateHiddenRunTests,
  removeTab,
  updateLeftAsideTabState,
  toggleWhiteBoard,
  updateRightAsideTabState,
  updateUserLog,
  updateCode,
  updateGetCode,
  updateGenerateQuestions,
  setTabLocked,
  updateLanguagesEnvState,
} = tabsStateSlice.actions;

export default tabsStateSlice.reducer;
